'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import NavigationMenu from './NavigationMenu';
import SocialIcons from './SocialIcons';
import { getReadableImageUrl } from '@/lib/directus'

interface HeaderData {
	logo?: {
		image_file?: {
			image?: {
				id?: string
				filename_download?: string
			}
		}
		alt?: string
	}
	phone?: {
		title?: string
	}
	social_links: {
		external_links_id: {
			title: string
			url: string
			label: string
			target: string
		}
	}[]
	navigation: {
		menu_items: Array<{
			menu_items_id: {
				label: string
				href: string
				submenu?: {
					label: string
					href: string
					submenu?: {
						label: string
						href: string
					}[]
				}[]
			}
		}>
	}
}

export const fields = [
	'logo.image_file.image.id',
	'logo.image_file.image.filename_download',
	'logo.alt',

	'phone.title',

	'social_links.external_links_id.title',
	'social_links.external_links_id.url',
	'social_links.external_links_id.target',

	'navigation.menu_items.menu_items_id.label',
	'navigation.menu_items.menu_items_id.href',
	'navigation.menu_items.menu_items_id.submenu.label',
	'navigation.menu_items.menu_items_id.submenu.href',
	'navigation.menu_items.menu_items_id.submenu.submenu.label',
	'navigation.menu_items.menu_items_id.submenu.submenu.href',
] as const

export default function Header({ data }: { data: HeaderData }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const Logo = ({ className = 'w-32 md:w-48 xl:w-[184px]' }) => {
    // Pre-calculate image source to avoid delays
    const logoImageSrc = data?.logo?.image_file?.image?.id && data?.logo?.image_file?.image?.filename_download && !logoError
      ? getReadableImageUrl(data.logo.image_file.image.id, data.logo.image_file.image.filename_download)
      : '/images/logo.svg';

    const logoAlt = data?.logo?.alt || 'Logo';

    const handleLinkClick = () => {
      setLogoError(false);
    }

    return (
      <div className='relative flex-shrink-0'>
        <a className='siteLogo' href='/' onClick={handleLinkClick}>
          <Image
            src={logoImageSrc}
            alt={logoAlt}
            width={184}
            height={58}
            className={`${className} h-auto`}
            priority
            unoptimized={logoImageSrc.startsWith('/images/')}
            onError={() => setLogoError(true)}
          />
        </a>
      </div>
    );
  };

  const phoneNumber = data?.phone?.title || '513-813-1400'

  const PhoneLink = ({ showNumber = false }) => (
    <Link
      href={`tel:${phoneNumber}`}
      className='flex items-center hover:opacity-80 transition-opacity mr-2 md:mr-0'
      aria-label={showNumber ? undefined : `Call ${phoneNumber}`}
    >
      {showNumber ? (
        <span className='text-base lg:text-xl font-brandon'>{phoneNumber}</span>
      ) : (
        <Image
          src='/images/phone.svg'
          alt=''
          width={24}
          height={24}
          className='w-5'
          aria-hidden='true'
        />
      )}
    </Link>
  )

  const HamburgerMenu = ({ className = '' }) => (
    <button
      className={`flex flex-col justify-between w-8 sm:w-10 md:w-12 h-5 sm:h-6 cursor-pointer group ${className}`}
      aria-label='Menu'
      onClick={() => {
        // Menu opening disabled for now
        setIsMenuOpen(!isMenuOpen)
      }}
    >
      <span
        className={`w-full h-[2px] bg-black transition-all duration-300 group-hover:bg-gray-600
          ${
            isMenuOpen
              ? 'rotate-45 translate-y-[9px] sm:translate-y-[11px] group-hover:bg-gray-600'
              : 'group-hover:w-3/4'
          }`}
      />
      <span
        className={`w-full h-[2px] bg-black transition-all duration-300 group-hover:bg-gray-600
          ${isMenuOpen ? 'opacity-0' : 'group-hover:w-1/2'}`}
      />
      <span
        className={`w-full h-[2px] bg-black transition-all duration-300 group-hover:bg-gray-600
          ${
            isMenuOpen
              ? '-rotate-45 -translate-y-[9px] sm:-translate-y-[11px] group-hover:bg-gray-600'
              : 'group-hover:w-3/4'
          }`}
      />
    </button>
  );

  const Divider = () => <div className='w-[1px] h-7 bg-black' />;

  return (
		<>
			<header className='w-full bg-neutral-50 sticky top-0 z-50'>
				<div className='container py-4 max-md:px-4'>
				
					<div className='flex justify-between items-center md:hidden'>
						{/* Social Icons */}
						{/*  <SocialIcons data={data} /> */}
						<PhoneLink />
						{/* Logo */}
						<Logo />

						<div className='flex items-center gap-3'>
							<HamburgerMenu />
						</div>
					</div>

					{/* Desktop Header */}
					<div className='hidden md:flex justify-between items-center'>
						{/* Logo */}
						<Logo />

						{/* Right Section */}
						<div className='flex items-center gap-6 xl:gap-[60px]'>
							{/* Phone Link */}
							<PhoneLink showNumber={true} />

							{/* Divider */}
							<Divider />

							{/* Social Icons */}
							<SocialIcons data={data} />

							{/* Divider */}
							<Divider />

							{/* Hamburger Menu */}
							<HamburgerMenu />
						</div>
					</div>
				</div>
			</header>

			<NavigationMenu
				isOpen={isMenuOpen}
				onClose={() => setIsMenuOpen(false)}
				data={data}
			/>
		</>
	)
}